import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [user, setUser] = useState(() => {
    try {
      // First try to get the new 'user' format
      let storedUser = localStorage.getItem('user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }

      // Migrate from old 'firstName' format
      const oldFirstName = localStorage.getItem('firstName');
      if (oldFirstName) {
        const migratedUser = { firstName: oldFirstName, lastName: '', email: '' };
        localStorage.setItem('user', JSON.stringify(migratedUser));
        localStorage.removeItem('firstName'); // Clean up old data
        return migratedUser;
      }

      // Default empty user
      return { firstName: '', lastName: '', email: '' };
    } catch (err) {
      console.error('Error parsing stored user data:', err);
      localStorage.removeItem('user');
      localStorage.removeItem('firstName');
      return { firstName: '', lastName: '', email: '' };
    }
  });
  const navigate = useNavigate();

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = useCallback(() => {
    setToken('');
    setUser({ firstName: '', lastName: '', email: '' });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/app');
  }, [navigate]);

  useEffect(() => {
    // Check if token exists and is valid on app load
    if (token) {
      try {
        // Decode token to check expiration (without verifying signature for client-side check)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        if (payload.exp < currentTime) {
          // Token is expired, logout
          logout();
          return;
        }
      } catch (err) {
        // Invalid token format, logout
        logout();
        return;
      }
    }

    // Add axios response interceptor to handle token expiration
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, logout and redirect to login
          logout();
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
